import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { EMPTY_ORG_ID } from "@/constants/org";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUpdateTicketType } from "@/services/ticket/ticket.mutations";
import { ticketQueryOptions } from "@/services/ticket/ticket.options";
import { TicketTypeDrawerContext } from "@/utils/ticket-type-detail/ticket-type-drawer-context";
import { showSuccessToast } from "@/utils/toast.utils";
import type { AdminTicketTypeFullPageTab } from "./AdminTicketTypeFullPage";
import { AdminTicketTypeFullPage } from "./AdminTicketTypeFullPage";

const FULL_PARENT =
	"/_authenticated/(admin-app)/_admin/admin/configuration/ticketTypes/$ticketTypeId/full" as const;

const SearchSchema = z.object({
	tab: z
		.enum(["details", "form", "lifecycle", "rules", "activity"])
		.optional()
		.default("details"),
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z.enum(["drawer", "modal", "full"]).optional().default("full"),
	ruleId: z.string().optional(),
	/** Editor open mode for the rule drawer on `/full/form/rules`. */
	ruleMode: z.enum(["create", "edit", "delete"]).optional(),
	/** Sub-tab inside a single rule's drawer/full view. Currently unused on form rules. */
	ruleTab: z.enum(["details", "activity"]).optional(),
});

export const Route = createFileRoute(FULL_PARENT)({
	component: AdminTicketTypeFullLayoutRoute,
	validateSearch: (search) => SearchSchema.parse(search),
});

function AdminTicketTypeFullLayoutRoute() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const params = useParams({ from: FULL_PARENT });
	const search = useSearch({ from: FULL_PARENT });
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const drawerCtx = useContext(TicketTypeDrawerContext);
	const updateMutation = useUpdateTicketType();

	// Use the full page's own search.mode — not drawerCtx.mode, which reads the
	// drawer route's search params and always defaults to "view" on the full route.
	const isEditMode = search.mode === "edit";

	const detailQuery = useQuery(
		ticketQueryOptions.ticketTypeDetail(params.ticketTypeId),
	);
	const ticketType = detailQuery.data ?? null;

	const formSegment = `/configuration/ticketTypes/${params.ticketTypeId}/full/form`;
	const onFormRoutes = pathname.includes(formSegment);
	const rulesSegment = `/configuration/ticketTypes/${params.ticketTypeId}/full/rules/`;
	const onRuleDetailRoute = pathname.includes(rulesSegment);

	const activeTab: AdminTicketTypeFullPageTab = onFormRoutes
		? "form"
		: search.tab;

	if (onRuleDetailRoute || onFormRoutes) {
		return <Outlet />;
	}

	const handleTabChange = (tab: AdminTicketTypeFullPageTab) => {
		if (tab === "form") {
			void navigate({
				to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
				params: { ticketTypeId: params.ticketTypeId },
				search: {
					tab: "form",
					mode: search.mode,
					presentation: search.presentation,
				},
			});
			return;
		}
		void navigate({
			to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
			params: { ticketTypeId: params.ticketTypeId },
			search: { tab, mode: search.mode, presentation: search.presentation },
		});
	};

	const handleBackToList = () => {
		void navigate({
			to: "/admin/configuration/ticketTypes",
		});
	};

	// Build a full-page-specific context that overrides navigation to stay within
	// the full page route instead of redirecting to the drawer.
	const fullPageContext = drawerCtx
		? {
				...drawerCtx,
				mode: (search.mode ?? "view") as "view" | "edit",
				setMode: (mode: "view" | "edit") => {
					void navigate({
						to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
						params: { ticketTypeId: params.ticketTypeId },
						search: { tab: "details", mode, presentation: search.presentation },
					});
				},
				isSaving: updateMutation.isPending,
				saveDraft: async () => {
					if (!drawerCtx.isValid) return;
					try {
						await updateMutation.mutateAsync({
							ticketTypeId: params.ticketTypeId,
							data: {
								name: drawerCtx.draftName.trim(),
								description: drawerCtx.draftDescription || null,
								icon: drawerCtx.draftIcon || null,
								typeId: params.ticketTypeId,
								statusLifecycleId: drawerCtx.ticketType.lifecycleId ?? null,
								locationRequired: drawerCtx.locationRequired,
							},
							organizationId: EMPTY_ORG_ID,
						});
						showSuccessToast({
							message: t("admin.ticketTypes.drawer.details.toast.saveSuccess"),
						});
						void navigate({
							to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
							params: { ticketTypeId: params.ticketTypeId },
							search: {
								tab: "details",
								mode: "view" as const,
								presentation: search.presentation,
							},
						});
					} catch {
						// Global mutation error handler in root-provider.tsx shows the toast
					}
				},
				cancelDraft: () => {
					drawerCtx.setDraftName(drawerCtx.ticketType.name ?? "");
					drawerCtx.setDraftDescription(drawerCtx.ticketType.description ?? "");
					drawerCtx.setDraftIcon(drawerCtx.ticketType.icon ?? "");
					void navigate({
						to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
						params: { ticketTypeId: params.ticketTypeId },
						search: {
							tab: "details",
							mode: "view" as const,
							presentation: search.presentation,
						},
					});
				},
			}
		: null;

	if (!ticketType && !detailQuery.isLoading && !detailQuery.isError) {
		return null;
	}

	if (!ticketType) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{detailQuery.isLoading
						? t("common.actions.loadingEllipsis")
						: t("admin.ticketTypes.drawer.errors.notFound")}
				</div>
			</div>
		);
	}

	return (
		<TicketTypeDrawerContext.Provider value={fullPageContext}>
			<AdminTicketTypeFullPage
				ticketType={ticketType}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				onBackToList={handleBackToList}
				isLoading={detailQuery.isLoading}
				isError={detailQuery.isError}
				isEditMode={isEditMode}
			>
				<Outlet />
			</AdminTicketTypeFullPage>
		</TicketTypeDrawerContext.Provider>
	);
}
